import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBell } from '@tabler/icons-react';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { Button, useToast } from './ui';

/**
 * Sino de notificações — comunicados não lidos (REQ-012 Adendo / AB#64).
 *
 * Botão fixo no canto superior direito; badge mostra a contagem de
 * comunicados não lidos. Painel lateral à direita tem duas abas:
 *   - "Não lidos" (padrão): lista pendentes; botão "Marcar como lido"
 *     remove o item da lista (UPSERT na API).
 *   - "Lidos": histórico do que já foi lido (somente leitura).
 *
 * Carrega "não lidos" no boot e a cada visibilitychange. "Lidos" é
 * lazy — só busca ao abrir a aba pela primeira vez (ou ao alternar).
 */

interface Comunicado {
  id: number;
  titulo: string;
  mensagem: string;
  publicado_em: string;
  /** Rota interna (ex: '/minha-conta/chamados/42'). Quando presente, o
   * item vira clicável e marca-lido em cima da navegação (AB#81). */
  link_destino?: string | null;
}

type Aba = 'nao_lidos' | 'lidos';

/**
 * Variante visual:
 *   - 'floating' (default): botão fixo no canto superior direito da tela
 *     — modo herdado, usado pelas rotas SEM AppShell (tenant `/dashboard`,
 *     `/minha-conta/*`, etc.). Continuará funcionando até a Fase 2 plugar
 *     o `TenantLayout` no router e a UI dele assumir.
 *   - 'embedded' [UI-F1]: renderiza só o ícone, sem positioning fixo,
 *     pra encaixar no topbar do AppShell. O drawer lateral continua
 *     `fixed` (sobrepõe a tela) porque a interação não muda.
 */
interface SinoNotificacoesProps {
  variant?: 'floating' | 'embedded';
}

export function SinoNotificacoes({ variant = 'floating' }: SinoNotificacoesProps = {}): JSX.Element | null {
  const navigate = useNavigate();
  const access_token = useAuthStore((s) => s.access_token);
  const usuario = useAuthStore((s) => s.usuario);
  const [items, setItems] = useState<Comunicado[]>([]);
  const [lidos, setLidos] = useState<Comunicado[]>([]);
  const [aberto, setAberto] = useState(false);
  const [aba, setAba] = useState<Aba>('nao_lidos');
  const [marcando, setMarcando] = useState<number | null>(null);
  const [carregandoLidos, setCarregandoLidos] = useState(false);
  // [AB#322] "Marcar todas como lidas".
  const [marcandoTodas, setMarcandoTodas] = useState(false);
  const { toast } = useToast();

  const carregar = useCallback(async () => {
    if (!access_token || !usuario) {
      setItems([]);
      return;
    }
    try {
      const res = await apiFetch('/minha-conta/comunicados');
      if (!res.ok) return;
      setItems((await res.json()) as Comunicado[]);
    } catch {
      /* ignore */
    }
  }, [access_token, usuario]);

  const carregarLidos = useCallback(async () => {
    if (!access_token || !usuario) {
      setLidos([]);
      return;
    }
    setCarregandoLidos(true);
    try {
      const res = await apiFetch('/minha-conta/comunicados/lidos');
      if (!res.ok) return;
      setLidos((await res.json()) as Comunicado[]);
    } catch {
      /* ignore */
    } finally {
      setCarregandoLidos(false);
    }
  }, [access_token, usuario]);

  // [AB#157] Polling 30s pro badge atualizar sem F5. Comunicados
  // criados por integrações (ex.: alerta de escalação da Atena)
  // ficavam parados até refresh manual. Mesmo padrão do
  // AdminComunicados:
  //   - Fetch imediato no mount
  //   - setInterval só dispara quando a aba está visível (sem ruído
  //     de requests com o usuário em outra aba)
  //   - visibilitychange faz refetch imediato ao voltar pra aba
  //     (cobre janela > 30s onde o interval ficou parado)
  useEffect(() => {
    void carregar();
    const iv = window.setInterval(() => {
      if (document.visibilityState === 'visible') void carregar();
    }, 30_000);
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void carregar();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [carregar]);

  // Carrega lidos sob demanda: ao abrir o painel já na aba lidos, ou
  // quando o usuário alterna para a aba lidos pela primeira vez.
  useEffect(() => {
    if (aberto && aba === 'lidos') void carregarLidos();
  }, [aberto, aba, carregarLidos]);

  if (!access_token || !usuario) return null;

  async function marcarLido(c: Comunicado): Promise<void> {
    setMarcando(c.id);
    try {
      const res = await apiFetch(`/minha-conta/comunicados/${c.id}/marcar-lido`, {
        method: 'POST',
      });
      if (res.ok) {
        setItems((arr) => arr.filter((x) => x.id !== c.id));
        // Atualiza o histórico de lidos para refletir imediatamente quando
        // o usuário trocar para a aba "Lidos".
        setLidos((arr) => [c, ...arr.filter((x) => x.id !== c.id)]);
      }
    } finally {
      setMarcando(null);
    }
  }

  /** [AB#322] Marca todos os não lidos de uma vez. Atualiza o estado local
   * (move `items` → `lidos`, zera o badge) — sem reload. */
  async function marcarTodasLidas(): Promise<void> {
    if (items.length === 0) return;
    setMarcandoTodas(true);
    try {
      const res = await apiFetch('/minha-conta/comunicados/marcar-todos-lidos', {
        method: 'POST',
      });
      if (!res.ok) throw new Error('falha');
      const idsMovidos = new Set(items.map((c) => c.id));
      setLidos((arr) => [...items, ...arr.filter((x) => !idsMovidos.has(x.id))]);
      setItems([]);
      toast('Todas as notificações marcadas como lidas.', 'success');
    } catch {
      toast('Erro ao marcar notificações.', 'error');
    } finally {
      setMarcandoTodas(false);
    }
  }

  /** Click no item com link_destino: marca como lido (não-bloqueante),
   * fecha o painel e navega via SPA. Best-effort — se o POST falhar, o
   * usuário ainda chega na URL. */
  function abrirComunicado(c: Comunicado): void {
    if (!c.link_destino) return;
    void apiFetch(`/minha-conta/comunicados/${c.id}/marcar-lido`, { method: 'POST' });
    setItems((arr) => arr.filter((x) => x.id !== c.id));
    setLidos((arr) => [c, ...arr.filter((x) => x.id !== c.id)]);
    setAberto(false);
    navigate(c.link_destino);
  }

  const total = items.length;
  const listaAtiva = aba === 'nao_lidos' ? items : lidos;

  // Classes do gatilho diferem por variant; o restante (drawer, listas,
  // marcação de leitura) é igual.
  const triggerClasses =
    variant === 'embedded'
      ? 'relative w-10 h-10 rounded-md grid place-items-center bg-transparent text-text-secondary hover:bg-bg-muted hover:text-text-primary transition-colors'
      : 'fixed right-4 top-4 z-40 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition hover:bg-slate-50';
  const badgeClasses =
    variant === 'embedded'
      ? 'absolute top-1 right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white ring-2 ring-bg-surface-v2'
      : 'absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white';

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-label="Notificações"
        className={triggerClasses}
      >
        <IconBell size={variant === 'embedded' ? 20 : 18} className={variant === 'embedded' ? '' : 'text-slate-600'} />
        {total > 0 && (
          <span className={badgeClasses}>
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {aberto && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/30"
            onClick={() => setAberto(false)}
          />
          <aside className="fixed right-0 top-0 z-50 flex h-full w-[380px] flex-col border-l border-slate-200 bg-white shadow-2xl">
            <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <p className="text-sm font-semibold text-slate-900">Notificações</p>
              <div className="flex items-center gap-2">
                {/* [AB#322] Só quando há não lidos. */}
                {total > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={marcandoTodas}
                    disabled={marcandoTodas}
                    onClick={() => void marcarTodasLidas()}
                  >
                    Marcar todas como lidas
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => setAberto(false)}
                  className="text-xl leading-none text-slate-400 hover:text-slate-800"
                  aria-label="Fechar"
                >
                  ×
                </button>
              </div>
            </header>
            <div role="tablist" className="flex border-b border-slate-200 px-2">
              <TabButton ativa={aba === 'nao_lidos'} onClick={() => setAba('nao_lidos')}>
                Não lidos {total > 0 && <Badge>{total}</Badge>}
              </TabButton>
              <TabButton ativa={aba === 'lidos'} onClick={() => setAba('lidos')}>
                Lidos
              </TabButton>
            </div>
            <div className="flex-1 overflow-auto">
              {aba === 'lidos' && carregandoLidos ? (
                <p className="px-5 py-10 text-center text-sm text-slate-400">Carregando…</p>
              ) : listaAtiva.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-slate-400">
                  {aba === 'nao_lidos'
                    ? 'Sem comunicados pendentes.'
                    : 'Nenhum comunicado no histórico.'}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {listaAtiva.map((c) => {
                    const temLink = aba === 'nao_lidos' && !!c.link_destino;
                    return (
                      <li
                        key={c.id}
                        className={`px-5 py-4 ${
                          temLink ? 'cursor-pointer transition hover:bg-slate-50' : ''
                        }`}
                        onClick={temLink ? () => abrirComunicado(c) : undefined}
                        role={temLink ? 'button' : undefined}
                        tabIndex={temLink ? 0 : undefined}
                        onKeyDown={
                          temLink
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  abrirComunicado(c);
                                }
                              }
                            : undefined
                        }
                      >
                        <p className="text-sm font-semibold text-slate-900">{c.titulo}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {new Date(c.publicado_em).toLocaleString('pt-BR')}
                        </p>
                        <p className="mt-2 whitespace-pre-line text-sm text-slate-700">
                          {c.mensagem}
                        </p>
                        {aba === 'nao_lidos' &&
                          (temLink ? (
                            <p className="mt-3 text-xs font-semibold text-brand-600">
                              Clique para abrir →
                            </p>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void marcarLido(c)}
                              disabled={marcando === c.id}
                              className="mt-3 text-xs font-semibold text-brand-600 hover:underline disabled:opacity-60"
                            >
                              {marcando === c.id ? 'Marcando…' : 'Marcar como lido'}
                            </button>
                          ))}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
}

function TabButton({
  ativa,
  onClick,
  children,
}: {
  ativa: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={ativa}
      onClick={onClick}
      className={`flex-1 px-4 py-2 text-xs font-semibold transition ${
        ativa
          ? 'border-b-2 border-brand-500 text-brand-700'
          : 'text-slate-500 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

function Badge({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
      {children}
    </span>
  );
}
